import { Pressable, StyleSheet, View } from 'react-native';
import { CheckCircle2, RotateCcw, XCircle } from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import type {
  PracticeMasteryStatus,
  PracticeQuestionViewModel,
} from '../../types/wrongQuestions';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type PracticeQuestionCardProps = {
  question: PracticeQuestionViewModel;
  selectedOptionId: string | null;
  submitted: boolean;
  masteryStatus: PracticeMasteryStatus;
  onSelectOption: (optionId: string) => void;
  onSubmit: () => void;
  onRetry: () => void;
  onMarkMastery: (status: PracticeMasteryStatus) => void;
  onOpenPhotoSearch: () => void;
  onBackToWrongBook: () => void;
};

const masteryOptions: PracticeMasteryStatus[] = ['未掌握', '模糊', '已掌握'];

export function PracticeQuestionCard({
  question,
  selectedOptionId,
  submitted,
  masteryStatus,
  onSelectOption,
  onSubmit,
  onRetry,
  onMarkMastery,
  onOpenPhotoSearch,
  onBackToWrongBook,
}: PracticeQuestionCardProps) {
  const answeredCorrectly = selectedOptionId === question.correctOptionId;

  return (
    <AppCard style={styles.card}>
      <View style={styles.header}>
        <View style={styles.titleCopy}>
          <AppText variant="subtitle" weight="bold">
            {question.title}
          </AppText>
          <AppText variant="caption" tone="muted">
            {question.subject} · {question.knowledgePoint} · {question.sourceLabel}
          </AppText>
        </View>
        <View style={styles.errorPill}>
          <AppText variant="caption" tone="danger" weight="bold">
            {question.errorType}
          </AppText>
        </View>
      </View>

      <View style={styles.promptBox}>
        <AppText variant="body">{question.prompt}</AppText>
      </View>

      <View style={styles.optionStack}>
        {question.options.map((option) => {
          const selected = selectedOptionId === option.id;
          const correct = submitted && option.id === question.correctOptionId;
          const wrong = submitted && selected && !correct;
          return (
            <Pressable
              key={option.id}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              disabled={submitted}
              onPress={() => onSelectOption(option.id)}
              style={[
                styles.optionRow,
                selected ? styles.optionSelected : null,
                correct ? styles.optionCorrect : null,
                wrong ? styles.optionWrong : null,
              ]}
            >
              <View style={styles.optionLabel}>
                <AppText variant="caption" weight="bold">
                  {option.label}
                </AppText>
              </View>
              <AppText variant="body" style={styles.optionContent}>
                {option.content}
              </AppText>
              {correct ? <CheckCircle2 size={18} color={tokens.colors.success} /> : null}
              {wrong ? <XCircle size={18} color={tokens.colors.danger} /> : null}
            </Pressable>
          );
        })}
      </View>

      {submitted ? (
        <View style={styles.resultBox}>
          <View style={styles.resultHeader}>
            {answeredCorrectly ? (
              <CheckCircle2 size={20} color={tokens.colors.success} />
            ) : (
              <XCircle size={20} color={tokens.colors.danger} />
            )}
            <AppText
              variant="body"
              weight="bold"
              tone={answeredCorrectly ? 'success' : 'danger'}
            >
              {answeredCorrectly ? '判分：正确' : '判分：需要复盘'}
            </AppText>
          </View>
          <AppText variant="caption" tone="primary" weight="bold">
            正确答案：{question.correctAnswer}
          </AppText>
          <View style={styles.explanationBox}>
            <AppText variant="caption" tone="muted" weight="bold">
              解析
            </AppText>
            <AppText variant="body">{question.explanation}</AppText>
          </View>
        </View>
      ) : null}

      <View style={styles.masterySection}>
        <AppText variant="body" weight="bold">
          掌握状态
        </AppText>
        <View style={styles.masteryRow}>
          {masteryOptions.map((status) => {
            const active = masteryStatus === status;
            return (
              <Pressable
                key={status}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                onPress={() => onMarkMastery(status)}
                style={[styles.masteryPill, active ? styles.masteryPillActive : null]}
              >
                <AppText
                  variant="caption"
                  weight="bold"
                  tone={active ? 'default' : 'primary'}
                  style={active ? styles.masteryPillTextActive : null}
                >
                  {status}
                </AppText>
              </Pressable>
            );
          })}
        </View>
      </View>

      <View style={styles.actions}>
        {!submitted ? (
          <AppButton label="提交答案" disabled={!selectedOptionId} onPress={onSubmit} />
        ) : (
          <AppButton
            label="再次练习"
            variant="secondary"
            onPress={onRetry}
            leftSlot={<RotateCcw size={16} color={tokens.colors.primary} />}
          />
        )}
        <View style={styles.actionRow}>
          <AppButton
            label="拍题答疑"
            variant="secondary"
            onPress={onOpenPhotoSearch}
            style={styles.actionButton}
          />
          <AppButton
            label="回错题本"
            variant="secondary"
            onPress={onBackToWrongBook}
            style={styles.actionButton}
          />
        </View>
      </View>
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.md,
  },
  titleCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  errorPill: {
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    backgroundColor: tokens.colors.dangerSoft,
  },
  promptBox: {
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  optionStack: {
    gap: tokens.spacing.sm,
  },
  optionRow: {
    minHeight: 54,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.surface,
  },
  optionSelected: {
    borderColor: tokens.colors.primary,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  optionCorrect: {
    borderColor: tokens.colors.success,
    backgroundColor: tokens.colors.successSoft,
  },
  optionWrong: {
    borderColor: tokens.colors.danger,
    backgroundColor: tokens.colors.dangerSoft,
  },
  optionLabel: {
    width: 30,
    height: 30,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.background,
  },
  optionContent: {
    flex: 1,
  },
  resultBox: {
    gap: tokens.spacing.sm,
    borderRadius: tokens.radius.lg,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.background,
  },
  resultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  explanationBox: {
    gap: tokens.spacing.xs,
  },
  masterySection: {
    gap: tokens.spacing.sm,
  },
  masteryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  masteryPill: {
    minHeight: 34,
    borderRadius: tokens.radius.full,
    paddingHorizontal: tokens.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  masteryPillActive: {
    backgroundColor: tokens.colors.primary,
  },
  masteryPillTextActive: {
    color: tokens.colors.white,
  },
  actions: {
    gap: tokens.spacing.md,
  },
  actionRow: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  actionButton: {
    flex: 1,
  },
});
